import {
  ICartAddressDetails,
  ICartDetailsPetCartItemData,
  ICartSearchResponseData,
  ICartUpdatePayloadPetItem,
  IHDCartAddressListResponseData,
} from "@/api_framework/api_modals/Cart";
import { useForm, UseFormReturn } from "react-hook-form";
import { ICartTableFormPetItem } from "../CartCreationTableComponent";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useUpdateCartDetailsAPI } from "@/app/hooks/api_hooks/Cart/useUpdateCartDetailsAPi";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ICartState } from "@/app_redux/reducers/slices/auth/cart_slice";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useCartItemDeleteAPI } from "@/app/hooks/api_hooks/Cart/useCartItemDeleteAPI";
import { useCartDetailsAPI } from "@/app/hooks/api_hooks/Cart/useCartDetailsAPI";
import { useEffect, useState } from "react";
import { useUtils } from "@/app/hooks/useUtils";
import { debounce, toNumber } from "lodash";
import clsx from "clsx";
import SimpleBadge from "@/app/components/ui/Badges/SimpleBadge";
import OfferCalloutBadge from "@/app/components/ui/Badges/OfferCalloutBadge";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import SearchableCouponSelectMenu from "@/app/components/ui/SelectMenus/SearchableCouponSelectMenu";
import { IDeleteCartItemModalState } from "@components/routes/modals/DeleteCartItemModal";
import { TrashIcon } from "@heroicons/react/24/outline";
import ExamptionModalComponent from "../../ClinicClosingComponent/ExamptionModalComponent";
import { useCartLevelCouponListAPI } from "@/app/hooks/api_hooks/Cart/useCartLevelCouponListAPI";
import { LOCKED_CHILD_ITEM_IDS, isMembershipExtensionCartItem } from "@/constants";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { toast } from "react-hot-toast";

export interface IPetCartTableRowFormFields {
  appliedCoupon: string;
}

// A gift line's gift, booking and coupon, so a lot update (item-search-click + cart update) keeps it a gift line
const getGiftLineFields = (row: ICartDetailsPetCartItemData) =>
  row?.gift_id
    ? { gift_id: row.gift_id, reference_id: row.reference_id ?? undefined, coupon_id: row.coupon_id ?? undefined }
    : {};

const PetCartTableRow: React.FC<{
  alreadyAddedURIDError: string | null;
  cartLevelCouponId: string;
  row: ICartDetailsPetCartItemData;
  fetchLotDetails: (
    item: ICartSearchResponseData,
    swap?: boolean,
    apply_membership_coupons?: boolean,
    editLotDetails?: boolean,
    isRefreshCart?: boolean,
  ) => void;
  rowIdKey: string;
  rowIndex: number;
  rowPetId: string;
  formHook: UseFormReturn<ICartTableFormPetItem>;
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  activeCartId: string;
  selectedAddress?: ICartAddressDetails | null;
  currentAddress?: IHDCartAddressListResponseData | null;
}> = ({
  alreadyAddedURIDError,
  cartLevelCouponId,
  row,
  activeCartId,
  rowIdKey,
  formHook,
  rowIndex,
  rowPetId,
  fetchLotDetails,
  currentAddress,
}) => {
  const { setVisible, setModalData } = useModalActions("CART_REMOVE_ITEM");
  const { execute: updateCartDetails } = useUpdateCartDetailsAPI();
  const { getRouteKey } = useRouter();
  const { activeCartDetails } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState.state as ICartState;
  const mappedClinicId = useAppSelector((state) => state?.root?.UIStates?.mappedClinicId);
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { customerId, petId } = routeState.state as ICustomerProfileState;
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  const { mappedClinicName } = useAppSelector((state) => state.root.UIStates);
  const { execute: logAPI } = useClickLogAPI();
  const { execute: deleteCartItem } = useCartItemDeleteAPI();
  const { execute: refetchCartDetails } = useCartDetailsAPI();
  const couponItems =
    row.all_applicable_coupons?.map((c) => ({
      title: c.coupon_name,
      id: c.coupon_id,
      state: { coupon_visibility: c.coupon_visibility },
    })) ?? [];
  const [highlightedRow, setHighlightedRow] = useState<boolean>(false);
  const { InvoiceStatusDataTransformed, veticDateFormat } = useUtils();
  const [examption, setExamption] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const { execute: fetchCartLevelCoupons } = useCartLevelCouponListAPI();

  // RBAC: CART_ROW_EDIT_ACTION — legacy roles: central_ops, clinic_ops_doggyworld
  const isCentralOpps = useFeatureAccess("CART_ROW_EDIT_ACTION");

  const cartRowFormHook = useForm<IPetCartTableRowFormFields>({
    defaultValues: {
      appliedCoupon: "",
    },
  });

  const debounceRemoveHighlight = debounce(() => {
    setHighlightedRow(false);
  }, 1500);

  const callLogAPIBase = (
    item: string,
    eventType: "CART_QUANTITY_PLUS" | "CART_QUANTITY_MINUS" | "CART_COUPON" | "CART_ITEM_DELETED",
  ) => {
    const logBody: IClickLogBody = {
      event_type: eventType,
      scr_id: "CART",
      client_id: customerId,
      emr_id: activeCartDetails?.emr_id,
      cart_id: activeCartId,
      pet_id: petId,
      emr_cart_items: activeCartDetails,
      emr_cart_click_item: item,
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      vms_user_id: userLogInfo,
      timestamp: Date.now(),
    };

    logAPI(logBody);
  };

  const callLogAPILab = (item: any) => callLogAPIBase(item, "CART_QUANTITY_PLUS");
  const callLogAPILabMINUS = (item: any) => callLogAPIBase(item, "CART_QUANTITY_MINUS");
  const callLogAPILabDeleted = (item: any) => callLogAPIBase(item, "CART_ITEM_DELETED");

  useEffect(() => {
    if (alreadyAddedURIDError) {
      if (row.unique_row_id === alreadyAddedURIDError) {
        setHighlightedRow(true);
        const rowElement = document.getElementById(`cart-item:${row.unique_row_id}`);
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: "smooth" });
        }
        debounceRemoveHighlight();
      }
    }
  }, [alreadyAddedURIDError]);

  const handleUpdateCartQty = (row: ICartDetailsPetCartItemData, qty: number) => {
    const petCartItems = formHook.getValues("items");
    const updatedCartItems = petCartItems
      ?.map((pc) => {
        if (pc.unique_row_id === row.unique_row_id) {
          const dewormingItemFlag =
            typeof pc?.is_deworming_item === "boolean"
              ? pc.is_deworming_item
              : typeof row?.is_deworming_item === "boolean"
                ? row.is_deworming_item
                : row?.is_deworming;
          return {
            coupon_id: cartRowFormHook.getValues("appliedCoupon"),
            id: row.row_id,
            availability: row?.availability ?? "",
            qty: qty,
            unique_row_id: row.unique_row_id,
            tag: row.tag,
            lot_details: pc?.lot_details ?? null,
            ...(typeof dewormingItemFlag === "boolean" ? { is_deworming_item: dewormingItemFlag } : {}),
            ...(row?.gift_id ? { gift_id: row.gift_id, reference_id: row.reference_id ?? undefined } : {}),
          };
        } else {
          return null;
        }
      })
      .filter((item) => item) as ICartUpdatePayloadPetItem[];
    updateCartDetails(
      {
        customer_id: customerId,
        pincode: currentAddress?.pincode,
        is_hd_stock: false,
        main_cart_items: [
          {
            vetic_variant_id: "",
            coupon_id: "",
            quantity: 0,
            unique_row_id: "",
            unit_price: 0,
          },
        ],
        cart_id: activeCartId,
        clinic_id: mappedClinicId,
        pets: [
          {
            items: updatedCartItems,
            pet_id: rowPetId,
            sync_version: activeCartDetails?.pets?.find((p) => p.pet_id === rowPetId)?.sync_version,
          },
        ],
      },
      "normal",
    ).then(() => {
      fetchCartLevelCoupons(activeCartId, mappedClinicId);
      refetchCartDetails(activeCartId, mappedClinicId, "normal", cartLevelCouponId, currentAddress?.pincode);
      fetchCartLevelCoupons(activeCartId, mappedClinicId);
    });
  };

  const validateCartItemQuantity = (rowIndex: number, cartItemQuantity: number, maxQuantity: number) => {
    if (cartItemQuantity <= 0) {
      formHook.setError(`items.${rowIndex}.qty`, {
        message: `!qty missing`,
      });
    } else if (cartItemQuantity > maxQuantity) {
      formHook.setError(`items.${rowIndex}.qty`, {
        message: `!qty missing`,
      });
    } else if (cartItemQuantity % 1 !== 0) {
      formHook.setError(`items.${rowIndex}.qty`, {
        message: "Invalid Qty",
      });
    } else {
      formHook.clearErrors(`items.${rowIndex}.qty`);
      handleUpdateCartQty(formHook.watch(`items.${rowIndex}`), formHook.watch(`items.${rowIndex}.qty`));
    }
  };

  const handleDeletePetCartRow = (rowItemId: string, urid: string, _itemName: any, reason_of_deletion: string) => {
    deleteCartItem(
      {
        cart_id: activeCartId,
        item_id: rowItemId,
        pet_id: rowPetId,
        unique_row_id: urid,
        reason_of_deletion: reason_of_deletion,
      },
      "normal",
    ).then(() => {
      fetchCartLevelCoupons(activeCartId, mappedClinicId);
      refetchCartDetails(activeCartId, mappedClinicId, "normal", cartLevelCouponId, currentAddress?.pincode);
      fetchCartLevelCoupons(activeCartId, mappedClinicId);
      callLogAPILabDeleted(rowItemId);
    });
  };

  useEffect(() => {
    const bestCoupon = row.all_applicable_coupons?.find((c) => c.is_selected);
    // A gift line is priced by the gift's coupon (coupon_id), while is_selected can still mark the line's earlier coupon
    cartRowFormHook.setValue("appliedCoupon", row?.gift_id && row?.coupon_id ? row.coupon_id : bestCoupon?.coupon_id);
  }, [row]);

  const fetchAppointmentData = (reason_of_deletion?: string) => {
    handleDeletePetCartRow(
      formHook.watch(`items.${rowIndex}.row_id`),
      formHook.watch(`items.${rowIndex}.unique_row_id`),
      null,
      reason_of_deletion ?? null,
    );
  };
  const isDewormingItem = Boolean(formHook.watch(`items.${rowIndex}.is_deworming_item`) ?? row?.is_deworming_item);
  const isDewormingRestricted = isDewormingItem && !isCentralOpps;
  const isBookingAddon = Boolean(formHook.watch(`items.${rowIndex}.is_booking_addon`) ?? row?.is_booking_addon);
  // A membership extension is billed as one unit, so its quantity is read only
  const isMembershipExtensionItem = isMembershipExtensionCartItem(
    formHook.watch(`items.${rowIndex}.tag`) ?? row?.tag,
  );

  return (
    <>
      <tr
        className={clsx(highlightedRow && "border-2 transition-all duration-300 border-blue-500 bg-blue-100")}
        id={`cart-item:${row.unique_row_id}`}
        key={rowIdKey}
      >
        <td className="break-words w-[300px] p-4 font-normal text-[#1c242f] text-xs">
          <div className="flex justify-start items-start flex-col gap-y-2">
            {InvoiceStatusDataTransformed(row.name)}
            <div className="flex flex-wrap justify-start items-center gap-x-2 gap-y-1">
              {row?.gift_id && row?.offer_callout && <OfferCalloutBadge callout={row.offer_callout} />}
              {row.source === 2 && (
                <SimpleBadge label={"EMR"} colorClassName={clsx("text-[#F75C03] bg-[#FFE7D9] ring-orange-500/10")} />
              )}
              {row.source === 4 && (
                <SimpleBadge
                  label={"Job Card"}
                  colorClassName={clsx("text-[#F75C03] bg-[#FFE7D9] ring-orange-500/10")}
                />
              )}
              {row.is_booking_addon === true && (
                <SimpleBadge
                  label={"Customer"}
                  colorClassName={clsx("text-[#854D0E] bg-[#FEF9C3] ring-yellow-600/30")}
                />
              )}
              {row.membership_pitch === true && (
                <SimpleBadge
                  label={"Special Offer - Eligible"}
                  colorClassName={clsx("text-[#0068FF] bg-[#EBF5FF] ring-blue-500/10")}
                />
              )}
            </div>
            {row?.virtual && row?.virtual === true && (
              <SimpleBadge label={`Left To Add ${row.virtual_qty ? row.virtual_qty : "NA"}`} />
            )}
          </div>
          {Object.values(row.lot_details)?.length > 0 || row?.edit_lot_details_option ? (
            <span className="text-[#8B909A] text-[8px]">
              {row.lot_details?.vetbuddy_lot_number} ,{veticDateFormat(row?.lot_details?.expiry_date ?? null)}
            </span>
          ) : null}
        </td>
        <td className="w-24 p-4 align-middle">
          <div className="relative flex flex-col items-center">
            {isMembershipExtensionItem || isDewormingRestricted ? (
              <p className="w-full text-center">{formHook.getValues(`items.${rowIndex}.qty`)}</p>
            ) : formHook.watch(`items.${rowIndex}.source`) === 2 &&
              ["#bom", "#service"].includes(formHook.watch(`items.${rowIndex}.tag`)) ? (
              <p className="w-full text-center">{formHook.getValues(`items.${rowIndex}.qty`)}</p>
            ) : formHook.watch(`items.${rowIndex}.source`) === 1 &&
              LOCKED_CHILD_ITEM_IDS.includes(row?.row_id) &&
              row?.applied_item_group_id &&
              !isCentralOpps ? (
              <p className="w-full text-center">{formHook.getValues(`items.${rowIndex}.qty`)}</p>
            ) : (
              <InputWithStartAndEndAddOn
                startAddon="-"
                disabled={(Object.keys(row.lot_details)?.length === 0 && row?.tag === "#product") || row?.is_disabled}
                startAddOnCallback={() => {
                  if (row.qty > 0) {
                    row.qty = row.qty - 1;
                    if (row.qty === 0) {
                      handleDeletePetCartRow(
                        formHook.watch(`items.${rowIndex}.row_id`),
                        formHook.watch(`items.${rowIndex}.unique_row_id`),
                        null,
                        null,
                      );
                    }
                    if (row.virtual && row.virtual === true) {
                      validateCartItemQuantity(rowIndex, toNumber(row.qty), row.virtual_qty);
                    } else {
                      validateCartItemQuantity(rowIndex, toNumber(row.qty), row.in_hand_qty);
                    }
                    callLogAPILabMINUS(row.row_id);
                  }
                }}
                step={1}
                endAddon="+"
                endAddOnCallback={() => {
                  if (row.virtual && row.virtual === true) {
                    if (row.qty === row.virtual_qty) {
                      formHook.setError(`items.${rowIndex}.qty`, {
                        message: `!qty not available`,
                      });
                      return;
                    }
                  }
                  if (row.qty === row.in_hand_qty) {
                    formHook.setError(`items.${rowIndex}.qty`, {
                      message: `!qty not available`,
                    });
                    fetchLotDetails(
                      {
                        id: row.row_id,
                        name: row.name,
                        stock_qty_with_text: row.availability,
                        tag: row.tag as any,
                        thumbnail: "",
                        category: row.category ?? "",
                        rowId: row.row_id,
                        urid: row.unique_row_id,
                        pet_id: rowPetId,
                        is_deworming_item:
                          typeof row?.is_deworming_item === "boolean" ? row.is_deworming_item : row?.is_deworming,
                        ...getGiftLineFields(row),
                      },
                      false,
                      false,
                      true,
                      true,
                    );
                    return;
                  }
                  formHook.setValue(`items.${rowIndex}.qty`, formHook.watch(`items.${rowIndex}.qty`) + 1);
                  validateCartItemQuantity(
                    rowIndex,
                    toNumber(formHook.watch(`items.${rowIndex}.qty`)),
                    formHook.watch(`items.${rowIndex}.in_hand_qty`),
                  );
                  callLogAPILab(row.row_id);
                }}
                min={1}
                max={
                  row?.virtual && row?.virtual === true
                    ? formHook.watch(`items.${rowIndex}.virtual_qty`)
                    : formHook.watch(`items.${rowIndex}.in_hand_qty`)
                }
                value={formHook.watch(`items.${rowIndex}.qty`)}
                onBlur={(e) => {
                  if (row?.virtual && row?.virtual === true) {
                    validateCartItemQuantity(
                      rowIndex,
                      toNumber(e.target.value),
                      formHook.watch(`items.${rowIndex}.virtual_qty`),
                    );
                  } else {
                    validateCartItemQuantity(
                      rowIndex,
                      toNumber(e.target.value),
                      formHook.watch(`items.${rowIndex}.in_hand_qty`),
                    );
                  }
                }}
                onChange={(e: any) => {
                  const regex = /^[0-9]*$/;
                  if (row?.virtual && row?.virtual === true) {
                    if (toNumber(e.target.value) > formHook.watch(`items.${rowIndex}.virtual_qty`)) {
                      formHook.setValue(`items.${rowIndex}.qty`, formHook.watch(`items.${rowIndex}.virtual_qty`));
                      return;
                    }
                  } else {
                    if (toNumber(e.target.value) > formHook.watch(`items.${rowIndex}.in_hand_qty`)) {
                      formHook.setValue(`items.${rowIndex}.qty`, formHook.watch(`items.${rowIndex}.in_hand_qty`));
                      return;
                    }
                    if (!regex.test(e.target.value)) {
                      formHook.setValue(`items.${rowIndex}.qty`, formHook.watch(`items.${rowIndex}.qty`));
                    } else {
                      formHook.setValue(`items.${rowIndex}.qty`, toNumber(e.target.value));
                    }
                  }
                }}
                type="string"
              />
            )}
            <span className="text-red-500 absolute mt-1 left-1/2 -translate-x-1/2 text-[10px] whitespace-nowrap">
              {formHook.formState.errors.items?.[rowIndex]?.qty?.message}
            </span>
          </div>
          <div className="text-[#0068FF] text-xs font-medium mt-2 flex flex-col">
            {Object.values(row.lot_details)?.length > 0 || row?.edit_lot_details_option
              ? null
              : row.tag === "#product" && (
                  <div>
                    <span className="text-[10px] font-medium text-[#F72F03] mt-1 whitespace-nowrap block">
                      Lot info missing!
                    </span>
                    <button
                      disabled={row?.is_disabled}
                      onClick={() => {
                        fetchLotDetails(
                          {
                            id: row.row_id,
                            name: row.name,
                            stock_qty_with_text: row.availability,
                            tag: row.tag as any,
                            thumbnail: "",
                            category: row.category ?? "",
                            rowId: row.row_id,
                            urid: row.unique_row_id,
                            pet_id: rowPetId,
                            is_deworming_item:
                              typeof row?.is_deworming_item === "boolean" ? row.is_deworming_item : row?.is_deworming,
                            ...getGiftLineFields(row),
                          },
                          true,
                          false,
                          true,
                        );
                      }}
                      className={clsx(
                        "text-blue-600 underline block whitespace-nowrap",
                        row?.is_disabled && "opacity-50 cursor-not-allowed",
                      )}
                    >
                      Edit Lot Details
                    </button>
                  </div>
                )}
          </div>
        </td>
        <td className="p-4 text-black font-light text-xs w-28">{formHook.watch(`items.${rowIndex}.unit_price`)}</td>
        <td className="p-4 text-black font-light text-xs w-28">{formHook.watch(`items.${rowIndex}.gross_price`)}</td>
        <td className="p-4 text-black font-light text-xs break-words w-64">
          <SearchableCouponSelectMenu
            errorMessages={[]}
            onSelectItem={(item) => {
              const couponId = item?.id || "";
              const selectedCouponId = cartRowFormHook.getValues("appliedCoupon");
              if (couponId === selectedCouponId) return;
              cartRowFormHook.setValue("appliedCoupon", couponId);
              handleUpdateCartQty(row, row.qty);
            }}
            filterOnEmptyQuery={(item) => item.state?.coupon_visibility !== false}
            onEnterPress={(typedValue) => {
              if (!typedValue) return;
              const exactMatch = row.all_applicable_coupons?.find(
                (c) => c.coupon_name?.toLowerCase() === typedValue.toLowerCase(),
              );
              if (exactMatch) {
                const currentSelected = cartRowFormHook.getValues("appliedCoupon");
                if (exactMatch.coupon_id === currentSelected) return;
                cartRowFormHook.setValue("appliedCoupon", exactMatch.coupon_id);
                handleUpdateCartQty(row, row.qty);
              } else {
                toast.error("Coupon not applicable");
              }
            }}
            optionRenderVariant="DEFAULT"
            disabled={row.is_disabled || isBookingAddon || couponItems?.length === 0}
            fieldError={formHook.formState.errors.items?.[rowIndex] as any}
            register={formHook.register(`items.${rowIndex}.coupon` as any, { required: false })}
            selectItems={couponItems}
            placeholder="Select Coupon"
            showTooltips={true}
            showTypedErrors={false}
            showDropdownIcon={true}
            withReset={true}
            defaultSelected={couponItems.find((oc) => oc.id === cartRowFormHook.watch("appliedCoupon"))}
            listBoxClassName="w-[360px]"
            className="text-gray-800 w-10"
            containerClassName="w-full"
          />
          <p className="text-[#8B909A] font-medium text-xs pl-1 pt-1">
            {formHook.watch(`items.${rowIndex}.selected_coupon` as any)}
          </p>
        </td>
        <td className="px-3 py-4 text-black font-light text-xs w-24">
          <div className="flex flex-col justify-start gap-1">
            <div className="text-left">{formHook.watch(`items.${rowIndex}.net_price`)}</div>

            <div className="px-1.5 py-1 bg-[#8adeb0]/20 rounded border border-[#05b152] justify-start items-center gap-2.5 flex">
              <div className="text-[#05b051] text-[10px] font-medium">₹{row?.discount_amount} off</div>
            </div>
          </div>
        </td>
        <td className="p-4 w-10">
          {row?.row_id === "f128dd76-f9e1-4a62-93a9-dcd041127c01" &&
          formHook.watch(`items.${rowIndex}.source`) === 1 ? (
            <button
              className={clsx(row.is_disabled && "opacity-50 cursor-not-allowed")}
              disabled={row.is_disabled}
              onClick={(e) => {
                e.preventDefault();
                if (formHook.watch(`items.${rowIndex}.booking_id`) && !isBookingAddon) {
                  setExamption(true);
                  setBookingDetails({
                    booking_id: formHook.watch(`items.${rowIndex}.booking_id`),
                    unique_row_id: formHook.watch(`items.${rowIndex}.unique_row_id`),
                  });
                } else if (
                  formHook.watch(`items.${rowIndex}.tag`) === "#service" ||
                  formHook.watch(`items.${rowIndex}.source`) === 4
                ) {
                  setModalData({
                    handleDeletePetCartRow: handleDeletePetCartRow,
                    rowItemId: formHook.watch(`items.${rowIndex}.row_id`),
                    urid: formHook.watch(`items.${rowIndex}.unique_row_id`),
                    itemName: formHook.watch(`items.${rowIndex}.name`),
                  } as IDeleteCartItemModalState);
                  setVisible();
                } else {
                  handleDeletePetCartRow(
                    formHook.watch(`items.${rowIndex}.row_id`),
                    formHook.watch(`items.${rowIndex}.unique_row_id`),
                    null,
                    null,
                  );
                }
              }}
            >
              <TrashIcon className="w-6 h-6 text-[#6d727d]" />
            </button>
          ) : isDewormingRestricted ? null : formHook.watch(`items.${rowIndex}.source`) === 2 &&
            ["#bom", "#service"].includes(formHook.watch(`items.${rowIndex}.tag`)) &&
            !isCentralOpps ? null : formHook.watch(`items.${rowIndex}.source`) === 1 &&
            LOCKED_CHILD_ITEM_IDS.includes(row?.row_id) &&
            row?.applied_item_group_id &&
            !isCentralOpps ? null : (
            <button
              className={clsx(row.is_disabled && "opacity-50 cursor-not-allowed")}
              disabled={row.is_disabled}
              onClick={(e) => {
                e.preventDefault();
                if (formHook.watch(`items.${rowIndex}.booking_id`) && !isBookingAddon) {
                  setExamption(true);
                  setBookingDetails({
                    booking_id: formHook.watch(`items.${rowIndex}.booking_id`),
                    unique_row_id: formHook.watch(`items.${rowIndex}.unique_row_id`),
                    item_name: formHook.watch(`items.${rowIndex}.name`),
                  });
                } else if (
                  formHook.watch(`items.${rowIndex}.tag`) === "#service" ||
                  formHook.watch(`items.${rowIndex}.source`) === 4
                ) {
                  setModalData({
                    handleDeletePetCartRow: handleDeletePetCartRow,
                    rowItemId: formHook.watch(`items.${rowIndex}.row_id`),
                    urid: formHook.watch(`items.${rowIndex}.unique_row_id`),
                    itemName: formHook.watch(`items.${rowIndex}.name`),
                  } as IDeleteCartItemModalState);
                  setVisible();
                } else {
                  handleDeletePetCartRow(
                    formHook.watch(`items.${rowIndex}.row_id`),
                    formHook.watch(`items.${rowIndex}.unique_row_id`),
                    null,
                    null,
                  );
                }
              }}
            >
              <TrashIcon className="w-6 h-6 text-[#6d727d]" />
            </button>
          )}
        </td>
      </tr>
      {examption && (
        <ExamptionModalComponent
          open={examption}
          setOpen={setExamption}
          fetchAppointmentData={fetchAppointmentData}
          bookingDetails={bookingDetails}
          source="day_closing_module"
        />
      )}
    </>
  );
};

export default PetCartTableRow;
